import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { catchError, map, Observable, of, switchMap } from 'rxjs';

import { DecreesService } from '../services/decrees.service';

import {
  fetchDecreeApprovals,
  fetchDecreeApprovalsFailure,
  fetchDecreeApprovalsSuccess,
  fetchDecreeAttachments,
  fetchDecreeAttachmentsFailure,
  fetchDecreeAttachmentsSuccess,
  fetchDecreeComments,
  fetchDecreeCommentsFailure,
  fetchDecreeCommentsSuccess,
  fetchDecreeFinancial,
  fetchDecreeFinancialFailure,
  fetchDecreeFinancialSuccess,
  fetchDecreeGeneral,
  fetchDecreeGeneralFailure,
  fetchDecreeGeneralSuccess,
  fetchDecreeHeader,
  fetchDecreeHeaderFailure,
  fetchDecreeHeaderSuccess,
  fetchDecreeImportedDecree,
  fetchDecreeImportedDecreeFailure,
  fetchDecreeImportedDecreeSuccess,
  fetchDecreeInfo,
  fetchDecreeInfoFailure,
  fetchDecreeInfoSuccess,
  fetchDecreeNotes,
  fetchDecreeNotesFailure,
  fetchDecreeNotesSuccess,
  fetchDecreePrimaryActions,
  fetchDecreePrimaryActionsFailure,
  fetchDecreePrimaryActionsSuccess,
  fetchDecreeRights,
  fetchDecreeRightsFailure,
  fetchDecreeRightsSuccess,
  fetchDecreeSidebar,
  fetchDecreeSidebarFailure,
  fetchDecreeSidebarSuccess,
  fetchDecreeWebcast,
  fetchDecreeWebcastFailure,
  fetchDecreeWebcastSuccess
} from './decrees.actions';

@Injectable()
export class DecreesEffects {
  constructor(
    private readonly actions$: Actions,
    private readonly decreesService: DecreesService
  ) {}

  fetchDecreeHeader$ = createEffect(
    (): Observable<Action> =>
      this.actions$.pipe(
        ofType(fetchDecreeHeader),
        switchMap((action) =>
          this.decreesService.fetchDecreeHeader(action.id).pipe(
            map((data) => fetchDecreeHeaderSuccess({ id: action.id, data })),
            catchError((error: HttpErrorResponse) => of(fetchDecreeHeaderFailure({ id: action.id, error })))
          )
        )
      )
  );

  fetchDecreeSidebar$ = createEffect(
    (): Observable<Action> =>
      this.actions$.pipe(
        ofType(fetchDecreeSidebar),
        switchMap((action) =>
          this.decreesService.fetchDecreeSidebar(action.id).pipe(
            map((data) => fetchDecreeSidebarSuccess({ id: action.id, data })),
            catchError((error: HttpErrorResponse) => of(fetchDecreeSidebarFailure({ id: action.id, error })))
          )
        )
      )
  );

  fetchDecreePrimaryActions$ = createEffect(
    (): Observable<Action> =>
      this.actions$.pipe(
        ofType(fetchDecreePrimaryActions),
        switchMap((action) =>
          this.decreesService.fetchDecreePrimaryActions(action.id).pipe(
            map((data) => fetchDecreePrimaryActionsSuccess({ id: action.id, data })),
            catchError((error: HttpErrorResponse) => of(fetchDecreePrimaryActionsFailure({ id: action.id, error })))
          )
        )
      )
  );

  fetchDecreeGeneral$ = createEffect(
    (): Observable<Action> =>
      this.actions$.pipe(
        ofType(fetchDecreeGeneral),
        switchMap((action) =>
          this.decreesService.fetchDecreeGeneral(action.id).pipe(
            map((data) => fetchDecreeGeneralSuccess({ id: action.id, data })),
            catchError((error: HttpErrorResponse) => of(fetchDecreeGeneralFailure({ id: action.id, error })))
          )
        )
      )
  );

  fetchDecreeAttachments$ = createEffect(
    (): Observable<Action> =>
      this.actions$.pipe(
        ofType(fetchDecreeAttachments),
        switchMap((action) =>
          this.decreesService.fetchDecreeAttachments(action.id).pipe(
            map((data) => fetchDecreeAttachmentsSuccess({ id: action.id, data })),
            catchError((error: HttpErrorResponse) => of(fetchDecreeAttachmentsFailure({ id: action.id, error })))
          )
        )
      )
  );

  fetchDecreeComments$ = createEffect(
    (): Observable<Action> =>
      this.actions$.pipe(
        ofType(fetchDecreeComments),
        switchMap((action) =>
          this.decreesService.fetchDecreeComments(action.id).pipe(
            map((data) => fetchDecreeCommentsSuccess({ id: action.id, data })),
            catchError((error: HttpErrorResponse) => of(fetchDecreeCommentsFailure({ id: action.id, error })))
          )
        )
      )
  );

  fetchDecreeApprovals$ = createEffect(
    (): Observable<Action> =>
      this.actions$.pipe(
        ofType(fetchDecreeApprovals),
        switchMap((action) =>
          this.decreesService.fetchDecreeApprovals(action.id).pipe(
            map((data) => fetchDecreeApprovalsSuccess({ id: action.id, data })),
            catchError((error: HttpErrorResponse) => of(fetchDecreeApprovalsFailure({ id: action.id, error })))
          )
        )
      )
  );

  fetchDecreeInfo$ = createEffect(
    (): Observable<Action> =>
      this.actions$.pipe(
        ofType(fetchDecreeInfo),
        switchMap((action) =>
          this.decreesService.fetchDecreeInfo(action.id).pipe(
            map((data) => fetchDecreeInfoSuccess({ id: action.id, data })),
            catchError((error: HttpErrorResponse) => of(fetchDecreeInfoFailure({ id: action.id, error })))
          )
        )
      )
  );

  fetchDecreeRights$ = createEffect(
    (): Observable<Action> =>
      this.actions$.pipe(
        ofType(fetchDecreeRights),
        switchMap((action) =>
          this.decreesService.fetchDecreeRights(action.id).pipe(
            map((data) => fetchDecreeRightsSuccess({ id: action.id, data })),
            catchError((error: HttpErrorResponse) => of(fetchDecreeRightsFailure({ id: action.id, error })))
          )
        )
      )
  );

  fetchDecreeFinancial$ = createEffect(
    (): Observable<Action> =>
      this.actions$.pipe(
        ofType(fetchDecreeFinancial),
        switchMap((action) =>
          this.decreesService.fetchDecreeFinancial(action.id).pipe(
            map((data) => fetchDecreeFinancialSuccess({ id: action.id, data })),
            catchError((error: HttpErrorResponse) => of(fetchDecreeFinancialFailure({ id: action.id, error })))
          )
        )
      )
  );

  fetchDecreeNotes$ = createEffect(
    (): Observable<Action> =>
      this.actions$.pipe(
        ofType(fetchDecreeNotes),
        switchMap((action) =>
          this.decreesService.fetchDecreeNotes(action.id).pipe(
            map((data) => fetchDecreeNotesSuccess({ id: action.id, data })),
            catchError((error: HttpErrorResponse) => of(fetchDecreeNotesFailure({ id: action.id, error })))
          )
        )
      )
  );

  fetchDecreeWebcast$ = createEffect(
    (): Observable<Action> =>
      this.actions$.pipe(
        ofType(fetchDecreeWebcast),
        switchMap((action) =>
          this.decreesService.fetchDecreeWebcast(action.id).pipe(
            map((data) => fetchDecreeWebcastSuccess({ id: action.id, data })),
            catchError((error: HttpErrorResponse) => of(fetchDecreeWebcastFailure({ id: action.id, error })))
          )
        )
      )
  );

  fetchDecreeImportedDecree$ = createEffect(
    (): Observable<Action> =>
      this.actions$.pipe(
        ofType(fetchDecreeImportedDecree),
        switchMap((action) =>
          this.decreesService.fetchDecreeImportedDecree(action.decreeId, action.importId).pipe(
            map((data) => fetchDecreeImportedDecreeSuccess({ id: action.decreeId, data })),
            catchError((error: HttpErrorResponse) =>
              of(fetchDecreeImportedDecreeFailure({ id: action.decreeId, error }))
            )
          )
        )
      )
  );
}
